import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getChatbot } from '../api';
import EmailResponderInbox from '../components/EmailResponderInbox';
import AppHeader from '../components/AppHeader';
import HeaderUtilityActions from '../components/HeaderUtilityActions';

type PreviewTab = 'chat' | 'email';

export default function ChatbotPreview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [chatbot, setChatbot] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<PreviewTab>('chat');
  const hasOpenedRef = useRef(false);

  useEffect(() => {
    loadChatbot();
    hasOpenedRef.current = false;
  }, [id]);

  const cleanupWidget = useCallback(() => {
    const script = document.getElementById('preview-widget-script');
    if (script) script.remove();
    document.querySelectorAll('.chat-widget-container').forEach((w) => w.remove());
    document.querySelectorAll('.chat-widget-error-container').forEach((w) => w.remove());
    document.querySelectorAll('style').forEach((style) => {
      if (style.textContent?.includes('chat-widget')) style.remove();
    });
  }, []);

  useEffect(() => {
    if (chatbot && activeTab === 'chat') {
      // Clean up any existing widget
      cleanupWidget();

      // Load widget script
      const script = document.createElement('script');
      script.id = 'preview-widget-script';
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      const widgetUrl = import.meta.env.VITE_WIDGET_URL || 'http://localhost:5174';
      script.src = `${widgetUrl}/chat-widget.js`;
      script.setAttribute('data-chatbot-id', chatbot.id);
      script.setAttribute('data-api-url', apiUrl);
      script.setAttribute('data-preview-mode', 'true');
      script.defer = true;

      script.onload = () => {
        if (!hasOpenedRef.current) {
          hasOpenedRef.current = true;
          setTimeout(() => {
            const button = document.querySelector('.chat-widget-button') as HTMLButtonElement;
            if (button) button.click();
          }, 500);
        }
      };

      document.body.appendChild(script);
    }

    if (activeTab === 'email') {
      cleanupWidget();
    }

    return () => {
      cleanupWidget();
    };
  }, [chatbot?.id, activeTab, cleanupWidget]);

  async function loadChatbot() {
    if (!id) return;
    try {
      const result = await getChatbot(id);
      if (result.success && result.data) {
        setChatbot(result.data);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to load chatbot:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner" />
        <p>Loading chatbot...</p>
      </div>
    );
  }

  if (!chatbot) {
    return null;
  }

  const tabStyle = (tab: PreviewTab) => ({
    fontSize: '15px',
    padding: '12px 18px',
    fontWeight: 600,
    border: 'none',
    background: 'transparent',
    borderBottom: activeTab === tab ? '3px solid var(--color-primary)' : '3px solid transparent',
    borderRadius: '0',
    color: activeTab === tab ? 'var(--color-primary)' : 'var(--color-text-secondary)',
    whiteSpace: 'nowrap' as const,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  });

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-bg-primary)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <AppHeader style={{
        background: 'var(--color-bg-white)',
        borderBottom: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-sm)'
      }} actions={<HeaderUtilityActions />}>
        <div className="flex flex-center gap-3">
          <Link to="/" className="nav-link nav-link-brand">
            <img src="/logo.png" alt="Neural Intelligence" className="header-logo" />
            <h1>Neural Intelligence</h1>
          </Link>
          <span className="breadcrumb-separator">/</span>
          <span style={{ color: 'var(--color-text-secondary)' }}>{chatbot.name}</span>
        </div>
      </AppHeader>

      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '32px 20px'
      }}>
        <div style={{
          maxWidth: '900px',
          width: '100%',
        }}>
          {/* Tab Navigation */}
          <div style={{ marginBottom: '24px', borderBottom: '2px solid var(--color-border)' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setActiveTab('chat')} style={tabStyle('chat')}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  Chat Widget
                </span>
              </button>
              <button onClick={() => setActiveTab('email')} style={tabStyle('email')}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  Email Responder
                </span>
              </button>
            </div>
          </div>

          {/* Chat Widget Tab */}
          {activeTab === 'chat' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                background: 'var(--color-bg-white)',
                borderRadius: '16px',
                padding: '48px 32px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.1)',
                marginBottom: '32px'
              }}>
                {chatbot.logo_url ? (
                  <div style={{
                    width: '64px',
                    height: '64px',
                    margin: '0 auto 24px',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }}>
                    <img
                      src={chatbot.logo_url}
                      alt={chatbot.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                ) : (
                  <div style={{
                    width: '64px',
                    height: '64px',
                    background: chatbot.theme_json ? JSON.parse(chatbot.theme_json).primaryColor : '#667eea',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
                  }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                )}

                <h1 style={{
                  fontSize: '36px',
                  fontWeight: 700,
                  marginBottom: '12px',
                  color: 'var(--color-text-primary)',
                  letterSpacing: '-0.02em'
                }}>
                  {chatbot.name}
                </h1>

                <p style={{
                  fontSize: '18px',
                  color: 'var(--color-text-secondary)',
                  marginBottom: 0,
                  lineHeight: '1.6'
                }}>
                  Click the chat widget in the bottom right to start a conversation
                </p>
              </div>

              <div style={{
                background: 'var(--color-bg-white)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid var(--color-border)'
              }}>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--color-text-secondary)',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  This is a live preview - try asking questions to see how the chatbot responds
                </p>
              </div>
            </div>
          )}

          {/* Email Responder Tab */}
          {activeTab === 'email' && (
            <EmailResponderInbox chatbotId={id!} chatbotName={chatbot.name} />
          )}
        </div>
      </div>
    </div>
  );
}
